import { fontSm, fontXs, padXs, radius } from './UITheme';

/** Ziel-Typ-Symbole für Fortschrittsbalken. */
const ICON_PLANET = '\u{1F30D}';
const ICON_HEX    = '\u2B21';
const ICON_SUN    = '\u2600\uFE0F';
const ICON_SHIP   = '\u{1F680}';

export interface TravelBarData {
  startIsPlanet: boolean;
  endIsPlanet: boolean;
  endIsSun: boolean;
  progress: number;           // 0..1
  label: string;              // z.B. "Flotte → Kepler-7b"
  eta: string;                // z.B. "02:34"
}

let _styleInjected = false;
function injectStyles(): void {
  if (_styleInjected) return;
  _styleInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes travelArrowSlide {
      from { background-position: 0 0; }
      to   { background-position: 20px 0; }
    }
    .travel-bar-track {
      position: relative;
      flex: 1;
      height: 10px;
      border-radius: 5px;
      background: #0a1525;
      border: 1px solid #1a3a5a;
      overflow: visible;
    }
    .travel-bar-fill {
      height: 100%;
      border-radius: 5px;
      background: linear-gradient(90deg, #1a3a6a, #2a6aaf);
      background-image: repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 4px,
        rgba(136,204,255,0.12) 4px,
        rgba(136,204,255,0.12) 8px
      );
      background-size: 20px 20px;
      animation: travelArrowSlide 1.2s linear infinite;
      transition: width 0.5s ease-out;
    }
    .travel-bar-ship {
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      font-size: 16px;
      filter: drop-shadow(0 0 4px rgba(136,204,255,0.6));
      transition: left 0.5s ease-out;
      z-index: 1;
      pointer-events: none;
    }
  `;
  document.head.appendChild(style);
}

export function createTravelProgressBar(data: TravelBarData): HTMLDivElement {
  injectStyles();

  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: padXs(), background: 'rgba(10,18,30,0.6)',
    borderRadius: radius(), border: '1px solid #1a2a3a',
    marginBottom: '6px',
  });

  // Start-Icon
  const startIcon = document.createElement('span');
  startIcon.textContent = data.startIsPlanet ? ICON_PLANET : ICON_HEX;
  startIcon.style.fontSize = '18px';
  startIcon.style.flexShrink = '0';

  // Track + Fill + Ship
  const track = document.createElement('div');
  track.className = 'travel-bar-track';

  const fill = document.createElement('div');
  fill.className = 'travel-bar-fill';
  fill.style.width = `${Math.max(0, Math.min(100, data.progress * 100))}%`;

  const ship = document.createElement('span');
  ship.className = 'travel-bar-ship';
  ship.textContent = ICON_SHIP;
  ship.style.left = `${Math.max(0, Math.min(100, data.progress * 100))}%`;

  track.appendChild(fill);
  track.appendChild(ship);

  // End-Icon
  const endIcon = document.createElement('span');
  endIcon.textContent = data.endIsSun ? ICON_SUN : data.endIsPlanet ? ICON_PLANET : ICON_HEX;
  endIcon.style.fontSize = '18px';
  endIcon.style.flexShrink = '0';

  // Label + ETA
  const info = document.createElement('div');
  Object.assign(info.style, {
    display: 'flex', flexDirection: 'column', minWidth: '80px',
    fontSize: fontXs(), color: '#6a8aaa', flexShrink: '0',
  });
  const lbl = document.createElement('span');
  lbl.textContent = data.label;
  lbl.style.fontSize = fontSm();
  lbl.style.color = '#8cf';
  const etaSpan = document.createElement('span');
  etaSpan.textContent = data.eta;

  info.appendChild(lbl);
  info.appendChild(etaSpan);

  row.appendChild(startIcon);
  row.appendChild(track);
  row.appendChild(endIcon);
  row.appendChild(info);

  return row;
}

/** Aktualisiert einen bestehenden Fortschrittsbalken (ohne DOM-Neuaufbau). */
export function updateTravelProgressBar(row: HTMLDivElement, data: TravelBarData): void {
  const fill = row.querySelector('.travel-bar-fill') as HTMLDivElement | null;
  const ship = row.querySelector('.travel-bar-ship') as HTMLSpanElement | null;
  const pct = `${Math.max(0, Math.min(100, data.progress * 100))}%`;
  if (fill) fill.style.width = pct;
  if (ship) ship.style.left = pct;

  // Update label + eta (last child → info div)
  const info = row.lastElementChild as HTMLDivElement | null;
  if (info && info.children.length >= 2) {
    (info.children[0] as HTMLSpanElement).textContent = data.label;
    (info.children[1] as HTMLSpanElement).textContent = data.eta;
  }
}
